import React, { useCallback, useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Badge,
  Button,
  Card,
  Icon,
  useConfirm,
  useToast,
} from "stories/Admin";
import DuaAiReviewDialog from "./DuaAiReviewDialog";
import { DUA_FIELD_LABELS, DuaAiRun, DuaTashkeelStats } from "./duaAiTypes";
import styles from "./DuaPage.module.scss";

interface DuaAiPanelProps {
  /** Kalles når godkjente forslag har endret duaene, så listen lastes på nytt. */
  onDuasChanged: () => void;
  categories: string[];
}

const formatTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("nb-NO", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

/**
 * AI-gjennomgang av duaene: start, fremdrift mens den går, og rapporten når den er ferdig.
 * Forslagene godkjennes i DuaAiReviewDialog.
 */
const DuaAiPanel: React.FC<DuaAiPanelProps> = ({
  onDuasChanged,
  categories,
}) => {
  const { execute, lastError } = useFetch();
  const { execute: startRun } = useFetch();
  const toast = useToast();
  const confirm = useConfirm();
  const [run, setRun] = useState<DuaAiRun | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [starting, setStarting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [tashkeel, setTashkeel] = useState<DuaTashkeelStats | null>(null);
  const [startingTashkeel, setStartingTashkeel] = useState(false);
  const { execute: fetchTashkeel } = useFetch();

  const loadTashkeel = useCallback(async () => {
    const result = (await fetchTashkeel(
      "/api/v1/admin/dua-ai/tashkeel",
      "GET",
    )) as DuaTashkeelStats | null;
    if (result) setTashkeel(result);
  }, []);


  const load = useCallback(async () => {
    const result = (await execute(
      "/api/v1/admin/dua-ai/runs/latest",
      "GET",
    )) as DuaAiRun | "" | null;
    if (result) setRun(result);
    else if (result === "") setRun(null);
    setLoaded(true);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Fremdriften hentes på nytt hvert 3. sekund mens jobben går
  const running = run?.status === "RUNNING";

  // Tashkeel-tallene hentes ved start og på nytt når en kjøring er ferdig
  useEffect(() => {
    if (!running) loadTashkeel();
  }, [running, loadTashkeel]);
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(load, 3000);
    return () => window.clearInterval(timer);
  }, [running, load]);

  const startTashkeel = async () => {
    const lacking = tashkeel ? tashkeel.none + tashkeel.partial : 0;
    const ok = await confirm({
      title: "Foreslå full tashkeel?",
      message: (
        <span>
          AI legger til fullstendige vokaltegn (fatha, kasra, damma, sukun,
          shadda, tanwin) på {lacking || "de"} duaer der arabisk mangler dem
          helt eller delvis, med Hisnul Muslim og Koranen som fasit der de
          finnes. Forslagene kommer i listen under og må godkjennes. Ventende
          forslag beholdes.
        </span>
      ),
      confirmLabel: "Foreslå tashkeel",
    });
    if (!ok) return;
    setStartingTashkeel(true);
    const result = (await startRun(
      "/api/v1/admin/dua-ai/runs/tashkeel",
      "POST",
    )) as DuaAiRun | null;
    setStartingTashkeel(false);
    if (result) {
      setRun(result);
      toast.success("AI foreslår tashkeel nå.");
    } else {
      await load();
      toast.error("Kunne ikke starte. Kjører en gjennomgang allerede?");
    }
  };

  const start = async () => {
    const ok = await confirm({
      title: "Start AI-gjennomgang av duaene?",
      message: (
        <span>
          AI går gjennom alle duaene mot Hisnul Muslim og Koranen: retter
          arabisk (tashkeel), oversettelser, referanser og titler, legger til
          engelsk og urdu, finner lydfiler og foreslår nye duaer. Det tar
          vanligvis 10–30 minutter. Ingenting endres før du godkjenner
          forslagene. Ubehandlede forslag fra forrige gjennomgang erstattes.
        </span>
      ),
      confirmLabel: "Start",
    });
    if (!ok) return;
    setStarting(true);
    const result = (await startRun(
      "/api/v1/admin/dua-ai/runs",
      "POST",
    )) as DuaAiRun | null;
    setStarting(false);
    if (result) {
      setRun(result);
      toast.success("AI-gjennomgangen er startet.");
    } else {
      await load();
      toast.error("Kunne ikke starte. Kjører den allerede?");
    }
  };

  if (!loaded && !run) return null;

  const summary = run?.summary;
  const percent =
    run && run.total > 0
      ? Math.min(100, Math.round((run.processed / run.total) * 100))
      : 0;
  const fieldCounts = Object.entries(summary?.fields ?? {}).filter(
    ([, n]) => n > 0,
  );
  const pending = (run?.pendingFixes ?? 0) + (run?.pendingNew ?? 0);

  return (
    <Card className={styles.aiPanel}>
      <div className={styles.itemHead}>
        <span className={styles.aiIcon} aria-hidden="true">
          <Icon name="spark" size={18} strokeWidth={2} />
        </span>
        <div className={styles.aiHeadText}>
          <b className={styles.itemTitle}>AI-gjennomgang</b>
          <span className={styles.aiSub}>
            Retter arabisk, oversettelser, referanser og titler, legger til
            engelsk og urdu, finner lydfiler og nye duaer.
          </span>
        </div>
        <Button size="sm" onClick={start} loading={starting} disabled={running}>
          {running ? "Kjører …" : run ? "Kjør på nytt" : "Start"}
        </Button>
      </div>

      {tashkeel && (
        <div className={styles.aiActions}>
          <span className={styles.aiSub}>
            Tashkeel: {tashkeel.full} av {tashkeel.total - tashkeel.empty}{" "}
            duaer med arabisk har full tashkeel · {tashkeel.partial} delvis ·{" "}
            {tashkeel.none} uten
            {tashkeel.empty > 0 && ` · ${tashkeel.empty} uten arabisk tekst`}
          </span>
          {tashkeel.none + tashkeel.partial > 0 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={startTashkeel}
              loading={startingTashkeel}
              disabled={running}
            >
              Foreslå tashkeel
            </Button>
          )}
        </div>
      )}

      {!run && lastError() && (
        <Alert tone="error">
          Fikk ikke hentet status for AI-gjennomgangen.
        </Alert>
      )}

      {run && running && (
        <div className={styles.aiProgress}>
          <div className={styles.aiProgressText}>
            <span>{run.phase ?? "Starter …"}</span>
            <span>
              {run.total > 0 ? `${run.processed} av ${run.total}` : ""}
            </span>
          </div>
          <div
            className={styles.aiBar}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label="Fremdrift"
          >
            <span style={{ width: `${percent}%` }} />
          </div>
          <span className={styles.aiSub}>
            Startet {formatTime(run.startedAt)}
            {run.startedBy ? ` av ${run.startedBy}` : ""}. Du kan lukke siden;
            jobben fortsetter på serveren.
          </span>
        </div>
      )}

      {run?.status === "FAILED" && (
        <Alert tone="error">
          Gjennomgangen stoppet: {run.error ?? "ukjent feil"}
        </Alert>
      )}

      {run?.status === "DONE" && summary && (
        <div className={styles.aiReport}>
          <span className={styles.aiSub}>
            Ferdig {formatTime(run.finishedAt)}
            {run.startedBy ? ` · startet av ${run.startedBy}` : ""}
          </span>
          <div className={styles.aiStats}>
            <div>
              <b>{summary.fixSuggestions ?? 0}</b>
              <span>duaer med rettelser</span>
            </div>
            <div>
              <b>{summary.newSuggestions ?? 0}</b>
              <span>nye duaer foreslått</span>
            </div>
            <div>
              <b>{summary.audioFound ?? 0}</b>
              <span>
                lydfiler funnet (av {summary.audioMissingBefore ?? 0} som
                manglet)
              </span>
            </div>
            <div>
              <b>{summary.unchanged ?? 0}</b>
              <span>duaer var allerede riktige</span>
            </div>
          </div>
          {fieldCounts.length > 0 && (
            <div className={styles.aiChips}>
              {fieldCounts.map(([field, n]) => (
                <Badge key={field} tone="blue">
                  {DUA_FIELD_LABELS[field] ?? field}: {n}
                </Badge>
              ))}
              {(summary.withWarnings ?? 0) > 0 && (
                <Badge tone="gold">Med advarsel: {summary.withWarnings}</Badge>
              )}
            </div>
          )}
          {summary.tashkeel && (
            <span className={styles.aiSub}>
              Tashkeel: {summary.tashkeel.suggested} nye forslag
              {summary.tashkeel.updatedPending > 0 &&
                `, ${summary.tashkeel.updatedPending} ventende forslag oppdatert`}{" "}
              av {summary.tashkeel.candidates} duaer som manglet full tashkeel
              {summary.tashkeel.unchanged > 0 &&
                ` · ${summary.tashkeel.unchanged} uten forbedring`}
              {summary.tashkeel.withWarnings > 0 &&
                ` · ${summary.tashkeel.withWarnings} med advarsel`}
            </span>
          )}
          <span className={styles.aiSub}>
            Matchet mot Hisnul Muslim: {summary.matchedHisn ?? 0} · Koranen:{" "}
            {summary.matchedQuran ?? 0} · Uten kilde: {summary.unmatched ?? 0}
            {(summary.skippedNotDua ?? 0) > 0 &&
              ` · ${summary.skippedNotDua} tekster fra Hisnul Muslim er ikke duaer og ble hoppet over`}
            {(summary.audioStillMissing ?? 0) > 0 &&
              ` · ${summary.audioStillMissing} duaer mangler fortsatt lyd`}
          </span>
          {(summary.errors?.length ?? 0) > 0 && (
            <div>
              <button
                type="button"
                className={styles.aiLink}
                onClick={() => setShowErrors((v) => !v)}
              >
                {showErrors ? "Skjul" : "Vis"} {summary.errors!.length} feil
                under kjøringen
              </button>
              {showErrors && (
                <ul className={styles.aiErrors}>
                  {summary.errors!.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className={styles.aiActions}>
            <span className={styles.aiSub}>
              Venter: {run.pendingFixes} rettelser og {run.pendingNew} nye ·
              Godkjent: {run.accepted} · Avvist: {run.rejected}
            </span>
            <Button
              size="sm"
              variant={pending > 0 ? "primary" : "secondary"}
              onClick={() => setReviewOpen(true)}
            >
              {pending > 0 ? "Gå gjennom forslagene" : "Se forslagene"}
            </Button>
          </div>
        </div>
      )}

      {run && reviewOpen && (
        <DuaAiReviewDialog
          run={run}
          categories={categories}
          onClose={() => {
            setReviewOpen(false);
            load();
          }}
          onChanged={() => {
            onDuasChanged();
            load();
            loadTashkeel();
          }}
        />
      )}
    </Card>
  );
};

export default DuaAiPanel;
